/**
 * Federated Cmd+K command palette (F-7.1, Phase 3 Day 4).
 *
 * Replaces the prior static-commands palette. Now drives live federated
 * search across 8 sources (findings / events / decisions / audit /
 * integrations / users / pages / help) via POST /v1/search/federated.
 *
 * Per docs/architecture/27-FEDERATED-COMMAND-PALETTE-DESIGN.md.
 */
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import {
  FederatedCommandPalette,
  loadRecentlyVisited,
  recordRecentlyVisited,
  type FederatedSearchResponse,
  type FederatedSearchResult,
} from '@foctta/ui-shell';

import { apiFetch } from '@/lib/api';

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
}) {
  const navigate = useNavigate();
  const [recentlyVisited, setRecentlyVisited] = useState<FederatedSearchResult[]>([]);

  // Hydrate recently-visited from localStorage when the palette opens.
  useEffect(() => {
    if (open) {
      setRecentlyVisited(loadRecentlyVisited());
    }
  }, [open]);

  // Cmd+K / Ctrl+K toggle binding.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onOpenChange]);

  const fetcher = useCallback(async (q: string): Promise<FederatedSearchResponse> => {
    return apiFetch<FederatedSearchResponse>('/v1/search/federated', {
      method: 'POST',
      body: JSON.stringify({ q, limit: 10 }),
    });
  }, []);

  const handleSelect = useCallback(
    (result: FederatedSearchResult) => {
      navigate({ to: result.url });
    },
    [navigate],
  );

  return (
    <FederatedCommandPalette
      open={open}
      onOpenChange={onOpenChange}
      fetcher={fetcher}
      onSelect={handleSelect}
      onRecentlyVisited={recordRecentlyVisited}
      recentlyVisited={recentlyVisited}
      placeholder="Search findings, activity, decisions, pages…"
    />
  );
}
